import React, { useLayoutEffect, useRef } from 'react';
import clsx from 'clsx';
import TextField from '@material-ui/core/TextField';
import { fade, makeStyles } from '@material-ui/core/styles';

const INVALID_DATE_MESSAGE = 'Invalid Date Format';

// The overlay has to line up character-for-character with the real input, whatever styles the parent applies.
const MIRRORED_STYLES = [
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'letterSpacing',
  'lineHeight',
  'textAlign',
  'textIndent',
];

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    display: 'block',
    flex: '1 1 auto',
    minWidth: 0,
  },
  input: {
    color: 'transparent',
    caretColor: theme.palette.text.primary,
    '&::selection': {
      color: 'transparent',
      backgroundColor: fade(theme.palette.primary.main, 0.2),
    },
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    whiteSpace: 'pre',
    pointerEvents: 'none',
    color: theme.palette.text.primary,
  },
  disabled: {
    color: theme.palette.text.disabled,
  },
  placeholder: {
    color: theme.palette.text.secondary,
  },
}));

// Passed as KeyboardDatePicker's TextFieldComponent. The picker's own text value / onChange are ignored;
// the input is driven by useEnhanceDateInput instead.
export default function DateSegmentField(props) {
  const { segmentInput, value, onChange, inputProps, InputProps, error, helperText, ...rest } = props;
  const hasOwnError = Boolean(error) || Boolean(helperText);

  return (
    <TextField
      {...rest}
      error={Boolean(error) || segmentInput.isInvalid}
      helperText={segmentInput.isInvalid && !hasOwnError ? INVALID_DATE_MESSAGE : helperText}
      value={segmentInput.text}
      onChange={() => {}}
      inputRef={segmentInput.inputRef}
      InputProps={{ ...InputProps, inputComponent: SegmentInput }}
      // eslint-disable-next-line react/jsx-no-duplicate-props
      inputProps={{ ...inputProps, ...segmentInput.inputProps, pieces: segmentInput.pieces }}
    />
  );
}

// A native input can only paint one text color, so the visible text is drawn by an overlay
// while the transparent input underneath keeps the caret, selection and keyboard handling.
function SegmentInput(props) {
  const { inputRef, pieces, className, disabled, ...rest } = props;
  const classes = useStyles();
  const ownInputRef = useRef(null);
  const overlayRef = useRef(null);

  useLayoutEffect(() => {
    if (!ownInputRef.current || !overlayRef.current) return;
    const computed = window.getComputedStyle(ownInputRef.current);
    MIRRORED_STYLES.forEach((property) => {
      overlayRef.current.style[property] = computed[property];
    });
  });

  function attachInput(element) {
    ownInputRef.current = element;
    if (typeof inputRef === 'function') inputRef(element);
    else if (inputRef) inputRef.current = element;
  }

  return (
    <span className={classes.root}>
      <input {...rest} ref={attachInput} disabled={disabled} className={clsx(className, classes.input)} />
      <span ref={overlayRef} aria-hidden="true" className={clsx(classes.overlay, disabled && classes.disabled)}>
        {(pieces || []).map((piece, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <span key={index} className={clsx(piece.isPlaceholder && classes.placeholder)}>
            {piece.text}
          </span>
        ))}
      </span>
    </span>
  );
}
